import getConfig from 'next/config'
import Cookies from 'universal-cookie'
import axios from 'axios'
import { default as UUID } from 'uuid'
import { isPlainObject } from 'lodash'
import { AnalyticsPayload } from '@utils/analytics/types'
import { isServer } from '../isServer'
import eventsList from '@data/optimizely-events.json'
import { getBrandIdentity } from '../getBrandIdentity'

/**
 * Takes a GA payload items array and counts up the raw quantity of all items added in the eCommerce data of the event.
 * We do this because we haven't come up with a clear way to handle GA ecommerce data in Optimizely events yet.
 * But we do want some baseline insight into how many items are being added to carts or are in carts at any given time.
 *
 * @param itemsList
 * @returns number
 */
const getRawItemQuantity = (itemsList: object[] | undefined | null): number => {
  if (!itemsList) {
    return 0
  }

  return itemsList.reduce((itemsCount: number, currItem: any) => {
    const quantity = currItem?.quantity

    // If quantity is set and is a number give back the quantity of the item. Otherwise tick quantity forward 1.
    if (quantity != null && !isNaN(quantity)) {
      return itemsCount + quantity
    }

    return itemsCount + 1
  }, 0)
}

/**
 * Optimizely expects (or at least most easily handles) a flat object of "event tags."
 * Which are basically just Google Analytics event payloads with any nested complexity filtered out.
 * So mainly we remove the 'event' field because that is the Optimizely event 'key' we submit.
 * Then we hoist the eCommerce event fields to the top of the object.
 * We flatten eCommerce item ids to a comma separated list.
 *
 * @param gaData
 * @returns flat Optimizely Event Data Object
 */
const generateOptlyTagFields = (gaData: AnalyticsPayload) => {
  const dropFields = ['event', 'ecommerce', 'items']

  const flattenedPayload = {}

  // Get all the top level payload key value pairs onto a cleaned object except ones we want excluded
  for (const key in gaData.payload) {
    if (!dropFields.includes(key)) {
      flattenedPayload[key] = gaData.payload[key]
    }
  }

  // Hoist all the ecommerce fields up to the main object unless it is something explicitly excluded.
  if (gaData.payload?.ecommerce) {
    for (const ecommKey in gaData.payload?.ecommerce) {
      if (!dropFields.includes(ecommKey)) {
        flattenedPayload[ecommKey] = gaData.payload?.ecommerce[ecommKey]
      }
    }
  }

  const itemIdsList =
    gaData.payload?.ecommerce?.items.reduce(
      (itemsAccumulate: string[], item: any) => {
        if (item.item_id) {
          itemsAccumulate.push(item.item_id)
        }
        return itemsAccumulate
      },
      []
    ) || []

  // If there are items on the event attach them as a comma separated list.
  if (itemIdsList) {
    flattenedPayload['item_ids'] = itemIdsList.join(',')
  }

  return flattenedPayload
}

export const sendOptimizelyEvent = (gaData: AnalyticsPayload) => {
  const {
    publicRuntimeConfig: {
      optimizelyAccountId,
      optimizelyFeatureFlagId,
      optimizelyDisableLogging,
    },
  } = getConfig()

  // Check is the events list from Optimizely is a valid javascript object.
  // Then if it isn't or the event submitted to this function isn't in the list we short circuit and exit early.
  const validEventObject = isPlainObject(eventsList)

  if (
    !validEventObject ||
    (validEventObject &&
      !Object.keys(eventsList).includes(gaData.payload.event))
  ) {
    !optimizelyDisableLogging &&
      console.warn(
        `Supplied event ${gaData.payload.event} for Google Analytics does not exist in Optimizely events list. No event will be send.`
      )

    return
  }

  // If Optimizely logging is enabled but no SDK key for feature flags is present, warn us about that when events try to fire.
  // Technically to submit an event you only need the accountID for your entire org. However all our events are attached to feature flag experiments.
  // So if the feature flag ID also isn't there we don't want to collect any events either.
  if (!optimizelyAccountId || !optimizelyFeatureFlagId) {
    !optimizelyDisableLogging &&
      console.warn(
        'No Optimizely events will be submitted to the API endpoint because no SDK key or Account ID is present.'
      )

    return
  }

  // Exit early if this runs on the server or Optimizely feature flag SDK ID isn't set.
  if (!optimizelyAccountId || !eventsList || isServer()) return

  const cookies = new Cookies(null, { path: '/' })
  const guidCookie = cookies.get('GUID')

  if (!guidCookie) {
    console.error(
      'No GUID cookie found in sendOptimizelyEvent. Skipping event send...'
    )

    return
  }

  const eventEntityId = eventsList[gaData.payload.event]?.id
  const flattenedOptlyTags = generateOptlyTagFields(gaData)

  /*
    OPTIMIZELY EVENT SEND API OBJECT STRUCTURE: https://docs.developers.optimizely.com/experimentation-data/reference/post_events

    This configs a JSON object compatible with an Axios request to send an event to the Optimizely event Logx endpoint.
    Certain things need contextually attached for our purposes so this won't be a perfect 1:1 of their docs, but it'll be close enough.
  */
  const optlyEventOpts = {
    method: 'POST',
    url: 'https://logx.optimizely.com/v1/events',
    headers: {
      'X-Optimizely-Strict': 'false',
      'content-type': 'application/json',
    },
    data: {
      anonymize_ip: true,
      visitors: [
        {
          snapshots: [
            {
              events: [
                {
                  key: gaData.payload.event, // Event 'key' in Optimizely is like the event name in Google Analytics
                  timestamp: Date.now(),
                  uuid: UUID.v4(),
                  tags: flattenedOptlyTags,
                  entity_id: eventEntityId,

                  // When eCommerce data for items total value is present we attach it to the event.
                  // Value is in whatever native currency format comes through. Revenue is the value in cents (So value * 100 cents)
                  ...(gaData.payload?.ecommerce?.value && {
                    value: gaData.payload?.ecommerce?.value,
                  }),
                  ...(gaData.payload?.ecommerce?.value && {
                    revenue: gaData.payload?.ecommerce?.value * 100,
                  }),
                  ...(gaData.payload?.ecommerce?.items && {
                    quantity: getRawItemQuantity(
                      gaData.payload?.ecommerce?.items
                    ),
                  }),
                },
              ],
            },
          ],
          visitor_id: guidCookie,
        },
      ],
      account_id: optimizelyAccountId,
      client_name: `next-commerce/${getBrandIdentity()}`,
    },
  }

  axios
    .request(optlyEventOpts)
    .then((res) => {
      if (!optimizelyDisableLogging && res.status !== 204) {
        console.error(
          `Optimizely event submission returned an unexpected non 204 status of: ${res.status}`
        )
        console.error('Optimizely event error response:', res)
      }
    })
    .catch((err) => {
      if (!optimizelyDisableLogging) {
        console.error(err)
      }
    })
}
